import { useState } from "react";
import axios from "axios";
import { Check, MessageCircle, X } from "lucide-react";
import { Reveal, FadeUp } from "@/components/invite/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RsvpSection({ invite }) {
  const { slug, family_name, passes, wedding } = invite;
  const [answer, setAnswer] = useState(null);
  const [sending, setSending] = useState(false);

  const respond = async (attending) => {
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/rsvp`, { slug, attending });
      setAnswer(attending);
      const msg = attending
        ? `¡Hola ${wedding.bride} y ${wedding.groom}! Somos la ${family_name} y confirmamos nuestra asistencia a vuestra boda (${passes} ${passes === 1 ? "pase" : "pases"}). ¡Nos vemos!`
        : `¡Hola ${wedding.bride} y ${wedding.groom}! Somos la ${family_name}. Lamentamos mucho no poder acompañaros en vuestra boda. ¡Os deseamos todo lo mejor!`;
      window.open(`https://wa.me/${wedding.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bg-olive px-6 sm:px-12 py-24 sm:py-32 text-center" data-testid="rsvp-section">
      <Reveal>
        <span className="overline-label text-nude">Confirmad vuestra asistencia</span>
      </Reveal>
      <Reveal delay={0.1} className="mt-8">
        <h2 className="font-serif text-5xl sm:text-7xl font-light tracking-tighter text-cream" data-testid="rsvp-family-name">
          {family_name}
        </h2>
      </Reveal>
      <FadeUp delay={0.2}>
        <p className="mx-auto mt-8 max-w-md text-sm sm:text-base text-cream/80 leading-relaxed">
          Tenéis <span className="font-serif italic text-xl text-cream">{passes}</span>{" "}
          {passes === 1 ? "pase asignado" : "pases asignados"}. Por favor confirmad vuestra asistencia
          antes del {wedding.rsvp_deadline}.
        </p>
      </FadeUp>
      <FadeUp delay={0.3}>
        {answer === null ? (
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => respond(true)}
              disabled={sending}
              className="group inline-flex items-center gap-3 rounded-full bg-cream px-10 py-4 text-xs font-medium uppercase tracking-[0.3em] text-olive transition-colors duration-300 hover:bg-nude hover:text-ink disabled:opacity-60"
              data-testid="rsvp-yes-btn"
            >
              <MessageCircle className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" strokeWidth={1.5} />
              Sí, ahí estaremos
            </button>
            <button
              onClick={() => respond(false)}
              disabled={sending}
              className="group inline-flex items-center gap-3 rounded-full border border-cream/40 px-10 py-4 text-xs font-medium uppercase tracking-[0.3em] text-cream/80 transition-colors duration-300 hover:border-cream hover:text-cream disabled:opacity-60"
              data-testid="rsvp-no-btn"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
              No podremos ir
            </button>
          </div>
        ) : (
          <div className="mt-12" data-testid="rsvp-registered-message">
            <p className="inline-flex items-center gap-3 rounded-full border border-cream/40 px-8 py-4 text-xs uppercase tracking-[0.25em] text-cream">
              <Check className="h-4 w-4 text-nude" strokeWidth={1.5} />
              {answer ? "Asistencia confirmada. ¡Gracias!" : "Respuesta registrada. ¡Gracias por avisar!"}
            </p>
            <button
              onClick={() => setAnswer(null)}
              className="block mx-auto mt-6 text-xs uppercase tracking-[0.25em] text-cream/60 underline-offset-4 hover:underline"
              data-testid="rsvp-change-btn"
            >
              Cambiar respuesta
            </button>
          </div>
        )}
      </FadeUp>
    </section>
  );
}
